import { memo, useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderCardWrapper } from '../../../FormBuilderCardWrapper';
import { FormBuilderElement } from '../../../FormBuilderElement';
import {
  useFormBuilderFields,
  useFormBuilderFieldWithError,
} from '../../../hooks';
import styles from './FormBuilderFile.module.css';
import { EFormBuilderFieldType, TFormBuilderFileField } from '@/types';

type TProps = {
  field: TFormBuilderFileField;
  index: number;
};

export const FormBuilderFile =
  /**
   * File upload field component for form builder with editable label
   * @param {TProps} props - Component props
   * @param {TFormBuilderFileField} props.field - File field data
   * @param {number} props.index - Field index in the form
   * @returns {JSX.Element} Form builder file upload field component
   */
  memo(({ field, index }: TProps) => {
    const { t } = useTranslation();
    const fileInputId = useId();

    const error = useFormBuilderFieldWithError<TFormBuilderFileField>(index);

    const [active, setActive] = useState(false);
    const onActiveChange = useCallback(
      (value: boolean) => setActive(value),
      [],
    );

    const { changeFields, removeField, copyField, moveDownField, moveUpField } =
      useFormBuilderFields(field);

    const changeFileFields = useCallback(
      (updated: Partial<TFormBuilderFileField>) => {
        changeFields({ ...field, ...updated });
      },
      [changeFields, field],
    );

    return (
      <FormBuilderCardWrapper
        variant={EFormBuilderFieldType.FILE}
        error={!!error}
        active={active}
        onActiveChange={onActiveChange}
        onRemove={removeField}
        onDownMove={moveDownField}
        onUpMove={moveUpField}
        onDuplicate={copyField}
      >
        <FormBuilderElement
          label={
            <FormBuilderElement.Label
              label={field.label}
              htmlFor={fileInputId}
              onLabelChange={(label) => changeFileFields({ label })}
              required={field.required}
              placeholder={t('form-builder.your-label-here')}
              error={!!error?.label}
            />
          }
          onRequiredChange={(required) => changeFileFields({ required })}
          required={field.required}
        >
          <FormBuilderElement.UploadFile
            id={fileInputId}
            disabled
            className={styles.file}
          />
        </FormBuilderElement>
      </FormBuilderCardWrapper>
    );
  });

FormBuilderFile.displayName = 'FormBuilderFile';
